// ShortcutHelp — `?` modal that lists every keyboard binding in Command Center.
//
// Renders a centered card over a dim backdrop. Closes on `?`, Esc, click on
// backdrop, or the X button. Bindings are grouped by category so Kimi can
// scan them quickly.
//
// This sits inside Center.tsx so it can read state about which shortcuts are
// currently active (e.g., "C is disabled while a drawer is open"), but the
// component itself takes no props — keep it dumb and presentational. Owners
// of the bindings stay where they are (StatusPill, ActionsPanel, etc.) so
// nothing has to be re-registered here.

import { useEffect } from 'react';

interface ShortcutHelpProps {
  open: boolean;
  onClose: () => void;
}

const GROUPS: { title: string; rows: { keys: string[]; label: string }[] }[] = [
  {
    title: 'Navigate',
    rows: [
      { keys: ['J'], label: 'Next thread in bucket' },
      { keys: ['K'], label: 'Previous thread in bucket' },
      { keys: ['Esc'], label: 'Back to Standup' },
    ],
  },
  {
    title: 'Act on this thread',
    rows: [
      { keys: ['1'], label: 'Fire primary suggested action' },
      { keys: ['2'], label: 'Fire secondary action' },
      { keys: ['3'], label: 'Fire third action' },
      { keys: ['C'], label: 'Jump to the reply composer' },
      { keys: ['S'], label: 'Message my team in Slack' },
    ],
  },
  {
    title: 'Status',
    rows: [
      { keys: ['O'], label: 'Mark Open' },
      { keys: ['A'], label: 'Mark Awaiting' },
      { keys: ['R'], label: 'Resolve' },
      { keys: ['Z'], label: 'Snooze\u2026' },
      { keys: ['!'], label: 'Mark Spam' },
    ],
  },
  {
    title: 'In the composer',
    rows: [
      { keys: ['Cmd', 'Enter'], label: 'Send' },
      { keys: ['Esc'], label: 'Discard draft' },
      { keys: ['/'], label: 'Slash commands (templates)' },
    ],
  },
  {
    title: 'Help',
    rows: [{ keys: ['?'], label: 'Toggle this help' }],
  },
];

export function ShortcutHelp({ open, onClose }: ShortcutHelpProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="shortcut-help-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="shortcut-help-title"
      onClick={onClose}
    >
      <div className="shortcut-help-card" onClick={(e) => e.stopPropagation()}>
        <div className="shortcut-help-header">
          <h2 id="shortcut-help-title">Keyboard shortcuts</h2>
          <button
            type="button"
            className="cc-icon-btn"
            aria-label="Close shortcut help"
            onClick={onClose}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden>
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="shortcut-help-body">
          {GROUPS.map((g) => (
            <div className="shortcut-group" key={g.title}>
              <div className="eyebrow">{g.title}</div>
              <ul>
                {g.rows.map((r) => (
                  <li key={r.label}>
                    <span className="shortcut-keys">
                      {r.keys.map((k, i) => (
                        <span key={k}>
                          <kbd>{k}</kbd>
                          {i < r.keys.length - 1 ? <span className="shortcut-plus">+</span> : null}
                        </span>
                      ))}
                    </span>
                    <span className="shortcut-label">{r.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
